<template>
  <div class="select-material">
    <NavHeaderBar :navBarTitle="$t('form.materialDelivery')" :showSearch="false" />
    <div v-show="!showSearch">

      <!-- Filter Section -->
      <div class="filter-section">
        <div class="filter-left">
          <div class="dropdown-trigger" @click="handleOper('brand')">
            <span class="label">{{ currentBrandLabel }}</span>
            <van-icon name="play" class="arrow-icon" :class="{ expanded: showBrandFilter }" />
          </div>
          <div class="dropdown-trigger" v-if="currentBrandValue" @click="handleOper('product')">
            <span class="label">{{ currentProductLabel }}</span>
            <van-icon name="play" class="arrow-icon" :class="{ expanded: showProductFilter }" />
          </div>
        </div>
        <van-icon name="search" class="search-icon" size="20" @click="handleSearch" />
      </div>
    </div>

    <!-- Search Bar -->
    <div v-show="showSearch" class="search-bar-wrapper">
      <van-search
        v-model="searchVal"
        show-action
        :placeholder="$t('button.search')"
        @input="onSearch"
        @cancel="onCancelSearch"
      />
    </div>

    <!-- Filter Popup -->
    <van-popup v-model="showBrandFilter" position="bottom" round safe-area-inset-bottom>
      <div class="filter-popup-content">
        <div class="filter-title">{{ $t('shared.filter.filter') }}</div>
        <div class="filter-options">
          <div
            v-for="option in projectList"
            :key="option.id"
            class="filter-tag"
            :class="{ active: currentBrandValue === option.id }"
            @click="selectTempFilter('brand', option)"
          >
            {{ option.projectName }}
          </div>
        </div>
        <div class="filter-actions">
          <van-button block class="reset-btn" @click="resetFilter('brand')">{{ $t('button.reset') }}</van-button>
          <van-button block type="info" class="confirm-btn" @click="confirmFilter('brand')">{{ $t('button.confirm') }}</van-button>
        </div>
      </div>
    </van-popup>
    <van-popup v-model="showProductFilter" position="bottom" round safe-area-inset-bottom>
      <div class="filter-popup-content">
        <div class="filter-title">{{ $t('shared.filter.filter') }}</div>
        <div class="filter-options">
          <div
            v-for="option in productList"
            :key="option.id"
            class="filter-tag"
            :class="{ active: currentProductValue === option.id }"
            @click="selectTempFilter('product', option)"
          >
            {{ option.productName }}
          </div>
        </div>
        <div class="filter-actions">
          <van-button block class="reset-btn" @click="resetFilter('product')">{{ $t('button.reset') }}</van-button>
          <van-button block type="info" class="confirm-btn" @click="confirmFilter('product')">{{ $t('button.confirm') }}</van-button>
        </div>
      </div>
    </van-popup>

    <!-- List Section -->
    <div class="list-content">
      <div v-for="(item) in materialDeliveryList" :key="item.id" class="material-item">
        <div class="left-content">
          <img v-if="item.imgUrl" :src="item.imgUrl" class="item-img" @click.stop="imgPreview(item.imgUrl.split(',')[0])" />
          <img v-else src="@/assets/images/icon_noimg.png" class="item-img" />
          <div class="item-info">
            <div class="title-row">
              <span class="title txt-ellipsis">{{ item.materielName }}</span>
              <van-icon v-if="item.isMain === 1" name="star" color="#e0bc38" class="star-icon" />
            </div>
            <div class="tags-row">
              <div class="product-name-item" v-if="item.materielType === 1">
                <div class="tag universal-name">{{ item.projectName }}</div>
                <div class="inventory">{{ $t('form.inv') }}: {{ item.inventoryCount }}</div>
              </div>
              <div class="product-name-item" v-else>
                <div class="tag" v-for="proItem in item.productName.split(',')" :key="proItem">{{ proItem }}</div>
                <div class="inventory">{{ $t('form.inv') }}: {{ item.inventoryCount }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="right-content">
          <van-checkbox v-model="item.isChoose" @click="handleCheck(item)" shape="square" />
        </div>
      </div>
      <!-- Empty State -->
      <van-empty v-if="materialDeliveryList.length === 0" :description="$t('tips.noData')" />
    </div>

    <!-- Footer Action -->
    <div class="footer-action">
      <div class="left-btn" @click="clearSelected">
        <span class="clear-text">{{ $t('button.clearSelected') }}</span>
        <span class="count-text">({{ $t('button.selected') }} {{ currentSelectedListCount }} {{ $t('button.types') }})</span>
        <!-- <span class="count-text" v-else>(Selected {{ selectedCount }} types)</span> -->
      </div>
      <div class="right-btn" @click="confirm">
        {{ $t('button.confirm') }}
      </div>
    </div>
  </div>
</template>

<script>
import NavHeaderBar from '@/components/NavHeaderBar'
import { arsEventDeliveryMaterielListPageApi, arsEventDeliveryBatchDeliveryMaterialApi, eventPolicyInfoApi } from '@/api/event'
import { mapState } from 'vuex'
// arsEventDeliveryBatchDeliveryMaterialApi
import { Toast, ImagePreview, Notify, Dialog } from 'vant'
import { arsMaterieQueryListApi } from '@/api/storeExpansion'
import { arsProjectAppQueryListApi } from '@/api/storeMaintain'

export default {
  name: 'SelectMaterial',
  components: {
    NavHeaderBar
  },
  data () {
    return {
      searchVal: '',
      showSearch: false,
      showBrandFilter: false,
      showProductFilter: false,
      currentBrandValue: '',
      currentProductValue: '',
      currentBrandLabel: this.$t('shared.filter.brand'),
      currentProductLabel: this.$t('shared.filter.product'),
      materialDeliveryList: [], // All data
      selectedIds: [], // Store selected IDs
      projectList: [],
      productList: [],
      pageType: '',
      currentSelectedList: []
    }
  },
  computed: {
    ...mapState('event', ['eventInfo']),
    ...mapState({
      globalData: 'globalData'
    }),
    selectedCount () {
      return this.materialDeliveryList.filter(item => item.isChoose).length
    },
    currentSelectedListCount () {
      return this.currentSelectedList.filter(item => item.isChoose).length
    }
  },
  async activated () {
    const { pageType, policyId } = this.$route.query
    this.pageType = pageType
    this.policyId = policyId || ''
    this.currentSelectedList = []
    this.onCancelSearch()
    if (['expandVisit', 'visit', 'reorder', 'sdVisit'].includes(this.pageType)) {
      const materialDelivery = JSON.parse(window.localStorage.getItem('materialDelivery'))
      this.currentSelectedList = materialDelivery || []
    }
    this.materialDeliveryList = []
    const requestApi = this.policyId ? eventPolicyInfoApi : arsProjectAppQueryListApi
    const brandRes = await requestApi(this.policyId)
    if (this.policyId) {
      brandRes.data.projectList.forEach(subItem => {
        subItem.id = subItem.projectId
      })
      this.projectList = brandRes.data.projectList
    } else {
      this.projectList = brandRes.data
    }
    this.resetFilter('brand')
  },
  methods: {
    handleOper (type) {
      if (type === 'brand') {
        this.showBrandFilter = !this.showBrandFilter
      } else {
        this.showProductFilter = !this.showProductFilter
      }
    },
    async initData () {
      if (['expandVisit', 'visit', 'reorder', 'sdVisit'].includes(this.pageType)) {
        const data = {
          materielName: this.searchVal,
          projectId: this.currentBrandValue,
          productIdList: this.currentProductValue,
          showLabel: this.pageType
        }
        Toast.loading({
          message: this.$t('shared.loading'),
          forbidClick: true,
          duration: 0
        })
        arsMaterieQueryListApi(data).then(res => {
          Toast.clear()
          res.data.forEach(subItem => {
            subItem.isChoose = false
            subItem.quantity = ''
            subItem.originalInventoryCount = Number(subItem.inventoryCount) || 0
          })
          this.materialDeliveryList = res.data || []
          if (this.currentSelectedList) {
            this.currentSelectedList.forEach(subItem => {
              this.materialDeliveryList.find(materItem => {
                if (subItem.id === materItem.id) {
                  materItem.quantity = subItem.quantity
                  materItem.isChoose = true
                  materItem.inventoryCount = Number(subItem.inventoryCount) || 0
                  return materItem
                }
              })
            })
          }
        })
      } else {
        const data = {
          eventId: this.eventInfo.eventId,
          projectIdList: this.currentBrandValue ? [this.currentBrandValue] : [],
          productIdList: this.currentProductValue,
          page: 1,
          limit: 1000,
          materielName: this.searchVal
        }
        Toast.loading({
          message: this.$t('shared.loading'),
          forbidClick: true,
          duration: 0
        })
        arsEventDeliveryMaterielListPageApi(data).then(res => {
          Toast.clear()
          const newList = res.data.list.filter(subItem => subItem.isChoose)
          newList.forEach(subItem => {
            // 用 id 判断 currentSelectedList 中是否已存在
            const exists = this.currentSelectedList.find(
              item => item.id === subItem.id
            )

            if (!exists) {
              this.currentSelectedList.push(subItem)
            }
          })
          // Initialize materialDeliveryList with API data
          this.materialDeliveryList = res.data.list || []
        })
      }
    },
    selectTempFilter (type, option) {
      if (type === 'brand') {
        this.currentBrandValue = option.id
        this.currentBrandLabel = option.projectName
        this.projectList.find(subItem => {
          if (subItem.id === option.id) {
            this.productList = subItem.productList
            return subItem
          }
        })
      } else {
        this.currentProductValue = option.id
        this.currentProductLabel = option.productName
      }
    },
    resetFilter (type) {
      if (type === 'brand') {
        this.currentBrandValue = ''
        this.currentBrandLabel = this.$t('shared.filter.brand')
        this.productList = []
      }
      this.currentProductValue = ''
      this.currentProductLabel = this.$t('shared.filter.product')
      this.confirmFilter()
    },
    confirmFilter (type) {
      if (type === 'brand') {
        this.currentProductValue = ''
        this.currentProductLabel = this.$t('shared.filter.product')
        this.showBrandFilter = false
      } else {
        this.showProductFilter = false
      }
      this.initData()
    },
    handleCheck (item) {
      // Checkbox v-model handles the toggle, this is for extra logic if needed
      if (item.isChoose) {
        this.currentSelectedList.push(item)
      } else {
        if (this.currentSelectedList.length <= 0) {
          this.currentSelectedList.push(item)
        } else {
          this.currentSelectedList = this.currentSelectedList.filter(subItem => {
            return subItem.id !== item.id
          })
        }
      }
    },
    clearSelected () {
      Dialog.confirm({
        message: this.$t('tips.messageFourteen'),
        confirmButtonText: this.$t('button.clear'),
        cancelButtonText: this.$t('button.cancel'),
        confirmButtonColor: '#d9001b'
      })
        .then(() => {
          this.materialDeliveryList.forEach((item) => {
            item.isChoose = false
          })
          this.currentSelectedList = []
          if (!['expandVisit', 'visit', 'reorder', 'sdVisit'].includes(this.pageType)) {
            this.confirm()
          }
        })
        .catch(() => {
          // on cancel
        })
    },
    confirm () {
      if (['expandVisit', 'visit', 'reorder', 'sdVisit'].includes(this.pageType)) {
        const materialDelivery = this.currentSelectedList.filter(item => item.isChoose)
        window.localStorage.setItem('materialDelivery', JSON.stringify(materialDelivery))
        this.$router.go(-1)
      } else {
        const data = {
          eventId: this.eventInfo.eventId,
          materialIdList: this.currentSelectedList.filter(item => item.isChoose).map(item => item.id)
        }
        Toast.loading({
          message: this.$t('shared.loading'),
          forbidClick: true,
          duration: 0
        })
        arsEventDeliveryBatchDeliveryMaterialApi(data).then(res => {
          Toast.clear()
          Notify({ type: 'success', message: this.$t('shared.success'), duration: 1500 })
          this.$router.go(-1)
        })
      }
    },
    onSearch () {
      // Triggered by search enter
      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
      }
      this.timer = setTimeout(() => {
        clearTimeout(this.timer)
        this.initData()
      }, 500)
    },
    onCancelSearch () {
      this.showSearch = false
      this.searchVal = ''
    },
    imgPreview (url) {
      ImagePreview([url])
    },
    handleSearch () {
      this.showSearch = true
      this.$nextTick(() => {
      // this.$refs.searchRef.focus()
        this.$el.querySelector('.van-field__control').focus()
        // this.$refs.searchRef.querySelector('input').focus()
      })
    }
  }
}
</script>

<style lang="less" scoped>
.select-material {
  min-height: 100vh;
  background-color: #fff;
  padding-bottom: 60px; // space for footer

  .filter-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 16px;
    background: #fff;
    border-bottom: 1px solid #eee;
    position: relative;
    z-index: 101; // Above popup

    .filter-left {
      display: flex;
      align-items: center;
    }

    .dropdown-trigger {
      display: flex;
      align-items: center;
      font-size: 13px;
      color: #333;
      cursor: pointer;

      .arrow-icon {
        font-size: 12px;
        margin-left: 6px;
        transform: rotate(90deg);
        transition: transform 0.2s;
        &.expanded {
          transform: rotate(-90deg);
        }
      }
    }

    .search-icon {
      color: #999;
      cursor: pointer;
    }
  }

  .search-bar-wrapper {
    background: #f7f8fa;
  }

  .list-content {
    padding: 0 16px;

    .material-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 0;
      border-bottom: 1px solid #eee;

      .left-content {
        display: flex;
        align-items: flex-start;
        flex: 1;
        margin-right: 10px;

        .item-img {
          width: 45px;
          height: 45px;
          object-fit: contain;
          margin-right: 5px;
        }

        .item-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          min-height: 40px;

          .title-row {
            display: flex;
            align-items: center;

            .title {
              font-size: 13px;
              color: #333;
              margin-right: 6px;
            }

            .star-icon {
              font-size: 12px;
            }
          }

          .tags-row {
            display: flex;
            align-items: center;
            font-size: 12px;

            .product-name-item {
              display: flex;
              align-items: center;
              flex-wrap: wrap;
            }

            .tag {
              padding: 1px 4px;
              border-radius: 2px;
              margin-right: 3px;
              margin-bottom: 3px;
              border: 1px solid #307bff;
              color: #307bff;

              &.universal-name {
                border: 1px solid #34b032;
                color: #34b032;
              }
            }

            .inventory {
              color: #666;
            }
          }
        }
      }

      .right-content {
        // Checkbox style
      }
    }
  }

  .footer-action {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50px;
    display: flex;
    box-shadow: 0 -2px 10px rgba(0,0,0,0.05);
    background: #fff;
    z-index: 100;

    .left-btn {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: #d93026;
      font-size: 14px;
      background: #fff;
      cursor: pointer;

      .clear-text {
        font-weight: bold;
      }
      .count-text {
        font-size: 10px;
      }
    }

    .right-btn {
      flex: 1;
      background: #1989fa;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      font-weight: bold;
      cursor: pointer;
    }
  }

  .filter-popup-content {
    padding: 16px;
    background: #fff;
    min-height: 200px;
    display: flex;
    flex-direction: column;

    .filter-title {
      font-size: 16px;
      font-weight: bold;
      margin-bottom: 20px;
      text-align: center;
      color: #333;
    }

    .filter-options {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      margin-bottom: 30px;
      flex: 1;

      .filter-tag {
        height: 33px;
        line-height: 33px;
        padding: 0px 16px;
        background: #f5f6f8;
        border-radius: 4px;
        font-size: 12px;
        color: #333;
        cursor: pointer;
        min-width: 80px;
        text-align: center;

        &.active {
          background: #e6f1fc;
          color: #1989fa;
          font-weight: 500;
        }
      }
    }

    .filter-actions {
      display: flex;
      gap: 12px;
      margin-top: auto;

      .reset-btn {
        flex: 1;
        background: #ebedf0;
        border: none;
        color: #333;
        border-radius: 6px;
      }

      .confirm-btn {
        flex: 1;
        border-radius: 6px;
      }
    }
  }
}
</style>
